import React from 'react';

import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';

import LoginCreds from '../../components/loginpages/LoginCreds';
import { LoginComponentProps } from '../../types/LoginFormData';
import { TSPCategory, TSPConfig } from '../types';

const TruckXInstructions: React.FC = () => (
  <>
    <p>
      To connect your TruckX account, follow these steps to share API access
      with Axle:
    </p>
    <ol>
      <li>Log into your TruckX admin account.</li>
      <li>
        Navigate to the <strong>API Key Transfer</strong> section in the
        left-hand menu.
      </li>
      <li>
        Expand the menu and click <strong>API Key Transfer</strong> again to
        open the sharing window.
      </li>
      <li>
        In the top-right corner, click the <strong>+ Share API Key</strong>{' '}
        button.
      </li>
      <li>
        In the pop-up window, select:
        <ul>
          <li>
            <strong>API Partner:</strong> Axle Technologies
          </li>
          <li>
            The appropriate <strong>Truck Number</strong>,{' '}
            <strong>Trailer Number</strong>, and <strong>Driver Name</strong>
          </li>
        </ul>
      </li>
      <li>
        Check the acknowledgment box and click <strong>Send API Key</strong> to
        authorize sharing.
      </li>
      <li>
        After sharing, the API key will be visible in your admin dashboard and
        can be revoked or extended as needed.
      </li>
      <li>
        Once complete, simply enter your <strong>DOT Number</strong> below. No
        additional credentials are required.
      </li>
    </ol>
    <p>
      If you're unsure about any of these steps, please contact TruckX support
      for assistance.
    </p>
  </>
);

const TruckXLogin: React.FC<LoginComponentProps> = ({
  onValidityChange,
  onHelpClick,
}) => {
  const [instructionsConfirmed, setInstructionsConfirmed] =
    React.useState(false);
  const [credentialsValid, setCredentialsValid] = React.useState(false);

  React.useEffect(() => {
    onValidityChange(credentialsValid && instructionsConfirmed);
  }, [credentialsValid, instructionsConfirmed, onValidityChange]);

  const handleCheckboxChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInstructionsConfirmed(event.target.checked);
    },
    []
  );

  const handleInstructionsClick = React.useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      onHelpClick?.();
    },
    [onHelpClick]
  );

  const handleCredentialsValidity = React.useCallback((isValid: boolean) => {
    setCredentialsValid(isValid);
  }, []);

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      <FormControlLabel
        sx={{
          m: 0,
          columnGap: 1.5,
          alignItems: 'flex-start',
          '& .MuiFormControlLabel-label': {
            color: 'text.primary',
            fontSize: 14,
            lineHeight: 1.5,
          },
        }}
        control={
          <Checkbox
            checked={instructionsConfirmed}
            onChange={handleCheckboxChange}
            inputProps={{ 'aria-label': 'Confirm TruckX API Partner setup' }}
            sx={{ mt: -0.5 }}
          />
        }
        label={
          <Typography component='span'>
            I have added Axle as an API Partner in the TruckX platform{' '}
            <Link
              href='#'
              onClick={handleInstructionsClick}
              sx={{
                fontWeight: 600,
                fontSize: 'inherit',
              }}
            >
              (instructions)
            </Link>
          </Typography>
        }
      />
      <LoginCreds
        disabled={!instructionsConfirmed}
        onValidityChange={handleCredentialsValidity}
        fields={[
          {
            label: 'DOT Number',
            name: 'dotnumber',
            type: 'number',
            required: true,
          },
        ]}
      />
    </Box>
  );
};

const truckxConfig: TSPConfig = {
  key: 'truckx',
  name: 'TruckX',
  category: TSPCategory.CUSTOM,
  helpText: <TruckXInstructions />,
  loginComponent: (props) => <TruckXLogin {...props} />,
};

export default truckxConfig;
